import { Injectable }       from '@angular/core';
import { NgbModal }         from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';

import { API }                                 from '../api/api.constant';
import { ApiService }                          from '../api/api.service';
import { BigDecimalService }                   from '../core/services/big-decimal.service';
import { CompanyIntegrationsModel }            from '../companies/models/company.integrations';
import { CompanyService }                      from '../companies/company.service';
import { ConfirmService }                      from '../messages/confirm/confirm.service';
import { CustomerPaymentModel }                from '../customer-payments/models/customer-payment';
import { CustomerPaymentsService }             from '../customer-payments/customer-payments.service';
import { InvoiceModel }                        from '../invoices/models/invoice';
import { SessionService }                      from '../sessions/session.service';
import { TillpaymentsPaymentModel }            from './models/tillpayments.payment';
import { TillpaymentsTerminalModel }           from './models/tillpayments.terminal';
import { TillpaymentsTransactionComponent }    from './tillpayments.transaction.component';
import { TillpaymentsTransactionModel }        from './models/tillpayments.transaction';
import { TillpaymentsTransactionTypeModel }    from './models/tillpayments.transaction-type';

@Injectable({
  providedIn: 'root'
})
export class TillpaymentsService {

  private _apiSettings     = API;

  private _transactionStatuses = {
    completed : 'COMPLETED',
    cancelled : 'CANCELLED',
    failed    : 'FAILED',
    pending   : 'PENDING',
    processing: 'PROCESSING',
    declined  : 'DECLINED',
    success   : 'SUCCESS'
  };

  public tableCacheKey  = 'tillpaymentsTerminals';

  constructor (
    private apiService             : ApiService,
    private bigDecimalService      : BigDecimalService,
    public companyService          : CompanyService,
    private confirmService         : ConfirmService,
    private customerPaymentsService: CustomerPaymentsService,
    private modalService           : NgbModal,
    private sessionService         : SessionService,
    private translateService       : TranslateService
  ) {}

  activateOnline () : ng.IPromise<boolean> {
    return this.confirmService.generic(this.translateService.instant('JS_SPACE.INTEGRATIONS.ACTIVATE', {
      name: this.translateService.instant('COMPANY_SPACE.INTEGRATIONS.TILLPAYMENTS.TILLPAYMENTS_ONLINE')
    }))
    .then(() => this.apiService.get('/company_integration/add_tillpayments_online'));
  }

  activatePos () : ng.IPromise<boolean> {
    return this.apiService.get('/company_integration/add_tillpayments_eft');
  }

  createTransaction (
    terminal : TillpaymentsTerminalModel,
    payment  : any,
    amount   : number,
    type     : TillpaymentsTransactionTypeModel = 'invoice',
    invoice ?: InvoiceModel
  ) : ng.IPromise<any> {
    let isRefund   = false;

    let objectId   : string;
    let objectType : string;

    switch (type) {
      case 'deposit':
        objectId   = payment.id;
        objectType = 'deposit';

        break;
      case 'payment':
        objectId   = payment.id;
        objectType = 'payment';

        break;
      case 'refund':
        isRefund   = true;
        objectId   = payment.id;
        objectType = 'deposit';

        break;
      default:
        objectId   = invoice.id;
        objectType = 'invoice';

        break;
    }

    const params : any = {
      amount,
      object_id  : objectId,
      object_type: objectType,
      terminal_id: terminal.terminalId
    };

    if (isRefund) {
      params.refund = 'true';
    }

    return this.apiService.post('/tillpayments/tillpayments_terminal_transaction', params);
  }

  deactivateOnline () : ng.IPromise<boolean> {
    return this.confirmService.generic(this.translateService.instant('JS_SPACE.INTEGRATIONS.DEACTIVATE', {
      name: this.translateService.instant('COMPANY_SPACE.INTEGRATIONS.TILLPAYMENTS.TILLPAYMENTS_ONLINE')
    }))
    .then(() => this.apiService.get('/company_integration/remove_tillpayments_online'));
  }

  deactivatePos () : ng.IPromise<boolean> {
    return this.confirmService.generic(this.translateService.instant('JS_SPACE.INTEGRATIONS.DEACTIVATE', {
      name: this.translateService.instant('COMPANY_SPACE.INTEGRATIONS.TILLPAYMENTS.TERMINAL_PAYMENTS')
    }))
    .then(() => this.apiService.get('/company_integration/remove_tillpayments_eft'));
  }

  didTransactionFail ( transaction : TillpaymentsTransactionModel ) : boolean {
    return transaction.eft_receipt.payment_status === this._transactionStatuses.cancelled
      || transaction.eft_receipt.payment_status === this._transactionStatuses.failed
      || transaction.eft_receipt.payment_status === this._transactionStatuses.declined
      || transaction.status_code !== '201';
  }

  doesLostTerminalPaymentExist ( payment : CustomerPaymentModel ) : Promise<boolean> | ng.IPromise<boolean> {
    return ( payment.id
      && payment.tillpayments_terminal_payment_intent_id
      && this.customerPaymentsService.isStatus(payment, 'open')
    ) ? this.apiService.get('/tillpayments/check_lost_transaction_status', payment.id)
        .then(response => response.status === 'SUCCESS')
      : Promise.resolve(false);
  }

  getDefaultUserTerminalId ( user = this.sessionService.user() ) : ng.IPromise<string> {
    return this.apiService.get('/users/get_terminal_id', user.id)
    .then(result => result.terminal_id);
  }

  getGatewayUrl ( invoice : InvoiceModel ) : ng.IPromise<string> {
    return this.apiService.get('/tillpayments/get_tillpayments_gateway_url', invoice.id)
    .then(( response : any ) => response.gateway_url);
  }

  getPayment ( invoice : InvoiceModel, token : string ) : ng.IPromise<TillpaymentsPaymentModel> {
    return this.apiService.get('/tillpayments/request_payment', [
      invoice.id,
      token
    ]);
  }

  getTerminals () : ng.IPromise<Array<TillpaymentsTerminalModel>> {
    return this.apiService.get('/tillpayments/get_tillpayments_terminal_type_list');
  }

  getTerminalDetails ( id : string ) : ng.IPromise<TillpaymentsTerminalModel> {
    return this.apiService.get('/tillpayments/get_tillpayments_terminal_details/', id);
  }

  getTransactionStatus ( transaction : TillpaymentsTransactionModel ) : ng.IPromise<TillpaymentsTransactionModel> {
    return this.apiService.get('/tillpayments/get_terminal_transaction_status', transaction.eft_receipt.id)
    .then(( response : any ) => {

      transaction.eft_receipt.payment_status = response.eft_receipt.payment_status;

      return transaction;
    });
  }

  hasSurcharges ( invoice : InvoiceModel ) : boolean {
    const surcharge = this.bigDecimalService.get(invoice.tillpayments_surcharge_amount);

    /**
     * Rails serializes decimal columns as strings, so '0.0' is read as "true".
     * Convert to number and compare so the surcharge totals block hides
     * cleanly after a refund clears the amount to zero.
    **/
    return surcharge.isGreaterThan(BigDecimal.ZERO);
  }

  isPosReady ( settings : CompanyIntegrationsModel ) : boolean {
    return this.companyService.hasIntegration('tillpayments-pos')
      && !!settings.tillpayments_terminal_api_key
      && !!settings.tillpayments_terminal_merchant_id;
  }

  isTerminalConnected ( terminal : TillpaymentsTerminalModel ) {
    return terminal.status.connectivity === 'AVAILABLE';
  }

  checkTerminalConnectivity ( terminal : TillpaymentsTerminalModel ) : ng.IPromise<any> {
    return this.getTerminalDetails(terminal.terminalId)
    .then(( freshTerminal : TillpaymentsTerminalModel ) => {
      return this.isTerminalConnected(freshTerminal)
        ? Promise.resolve(freshTerminal)
        : Promise.reject(this.translateService.instant('COMPANY_SPACE.INTEGRATIONS.TILLPAYMENTS.NO_ACTIVE_TERMINALS'));
    });
  }

  isTransactionApproved ( transaction : TillpaymentsTransactionModel ) : boolean {
    return transaction.eft_receipt.payment_status === this._transactionStatuses.success;
  }

  openPaymentPortal ( payment : TillpaymentsPaymentModel, invoice : InvoiceModel ) : void {
    this.getGatewayUrl(invoice)
    .then(( url : string ) => {
      const form = document.createElement('form');

      form.action        = url;
      form.method        = 'POST';
      form.style.display = 'none';

      for ( const key in payment ) {
        if (payment.hasOwnProperty(key)) {
          const input = document.createElement('input');

          input.type  = 'hidden';
          input.name  = key;
          input.value = payment[key];

          form.appendChild(input);
        }
      }

      document.body.appendChild(form);

      form.submit();
    });
  }

  processSurchargeAndPayment ( payment : any ) : ng.IPromise<any> {
    return this.apiService.post('/tillpayments/process_surcharge_and_payment', {
      object_id  : payment.id,
      object_type: 'payment',
      type       : 'payment'
    })
    .then(( processed : CustomerPaymentModel ) => this.waitForPaymentToClose(processed));
  }

  processTransaction (
    payment      : any,
    amount       : number,
    type         : TillpaymentsTransactionTypeModel = 'invoice',
    terminal    ?: TillpaymentsTerminalModel,
    invoice     ?: InvoiceModel,
  ) : ng.IPromise<any> {
    return Promise.resolve()
    .then(() => {
      return terminal
        ? Promise.resolve(terminal)
        : this.getDefaultUserTerminalId()
        .then(( terminalId : string ) => {
          const settings = this.sessionService.company().tillpayments_settings;

          if (!terminalId && !settings.tillpayments_terminal_id) {
            return Promise.reject(this.translateService.instant('COMPANY_SPACE.INTEGRATIONS.TILLPAYMENTS.NO_ACTIVE_TERMINALS'));
          }

          return this.getTerminalDetails(terminalId || settings.tillpayments_terminal_id);
        });
    })
    .then(( terminal : TillpaymentsTerminalModel ) => {
      return this.isTerminalConnected(terminal)
        ? Promise.resolve(terminal)
        : Promise.reject(this.translateService.instant('COMPANY_SPACE.INTEGRATIONS.TILLPAYMENTS.NO_ACTIVE_TERMINALS'));
    })
    .then(( terminal : TillpaymentsTerminalModel ) => {
      const modal = this.modalService.open(TillpaymentsTransactionComponent);

      modal.componentInstance.amount   = amount;
      modal.componentInstance.payment  = payment;
      modal.componentInstance.type     = type;
      modal.componentInstance.terminal = terminal;

      if ( type !== 'payment' ) {
        modal.componentInstance.invoice  = invoice;
      }

      return modal.result;
    })
    .then(( transaction : TillpaymentsTransactionModel ) => {
      return this.apiService.post('/tillpayments/process_surcharge_and_payment',
        {
          object_id  : type === 'payment' ? payment.id : invoice.id,
          object_type: type === 'payment' ? type : 'invoice',
          type       : type
        }
      )
      .then(( processed : any ) => type === 'payment' ? this.waitForPaymentToClose(processed) : processed)
      .then(() => transaction);
    })
    .then(( response : TillpaymentsTransactionModel ) => response.eft_receipt);
  }

  processDepositTransaction (
    deposit      : any,
    amount       : number,
    terminal    ?: TillpaymentsTerminalModel,
    invoice     ?: InvoiceModel
  ) : ng.IPromise<any> {
    return Promise.resolve()
    .then(() => {
      return terminal
        ? Promise.resolve(terminal)
        : this.getDefaultUserTerminalId()
        .then(( terminalId : string ) => {
          const settings = this.sessionService.company().tillpayments_settings;

          if (!terminalId && !settings.tillpayments_terminal_id) {
            return Promise.reject(this.translateService.instant('COMPANY_SPACE.INTEGRATIONS.TILLPAYMENTS.NO_ACTIVE_TERMINALS'));
          }

          return this.getTerminalDetails(terminalId || settings.tillpayments_terminal_id);
        });
    })
    .then(( terminal : TillpaymentsTerminalModel ) => {
      return this.isTerminalConnected(terminal)
        ? Promise.resolve(terminal)
        : Promise.reject(this.translateService.instant('COMPANY_SPACE.INTEGRATIONS.TILLPAYMENTS.NO_ACTIVE_TERMINALS'));
    })
    .then(( terminal : TillpaymentsTerminalModel ) => {
      const modal = this.modalService.open(TillpaymentsTransactionComponent);

      modal.componentInstance.amount   = amount;
      modal.componentInstance.payment  = deposit;
      modal.componentInstance.type     = 'deposit';
      modal.componentInstance.terminal = terminal;
      modal.componentInstance.invoice  = invoice;

      return modal.result;
    })
    .then(() => {
      return this.apiService.post('/tillpayments/process_terminal_deposit_success', {
        deposit_id: deposit.id,
        invoice_id: invoice.id
      });
    });
  }

  processDepositRefundTransaction (
    deposit      : any,
    amount       : number,
    terminal    ?: TillpaymentsTerminalModel,
    invoice     ?: InvoiceModel
  ) : ng.IPromise<any> {
    return Promise.resolve()
    .then(() => {
      return terminal
        ? Promise.resolve(terminal)
        : this.getDefaultUserTerminalId()
        .then(( terminalId : string ) => {
          const settings = this.sessionService.company().tillpayments_settings;

          if (!terminalId && !settings.tillpayments_terminal_id) {
            return Promise.reject(this.translateService.instant('COMPANY_SPACE.INTEGRATIONS.TILLPAYMENTS.NO_ACTIVE_TERMINALS'));
          }

          return this.getTerminalDetails(terminalId || settings.tillpayments_terminal_id);
        });
    })
    .then(( terminal : TillpaymentsTerminalModel ) => {
      return this.isTerminalConnected(terminal)
        ? Promise.resolve(terminal)
        : Promise.reject(this.translateService.instant('COMPANY_SPACE.INTEGRATIONS.TILLPAYMENTS.NO_ACTIVE_TERMINALS'));
    })
    .then(( terminal : TillpaymentsTerminalModel ) => {
      const modal = this.modalService.open(TillpaymentsTransactionComponent);

      modal.componentInstance.amount   = amount;
      modal.componentInstance.payment  = deposit;
      modal.componentInstance.type     = 'refund';
      modal.componentInstance.terminal = terminal;
      modal.componentInstance.invoice  = invoice;

      return modal.result;
    })
    .then(() => {
      return this.apiService.post('/tillpayments/process_terminal_deposit_refund', {
        deposit_id: deposit.id,
        invoice_id: invoice.id
      });
    });
  }

  saveSettings ( settings : CompanyIntegrationsModel ) : ng.IPromise<CompanyIntegrationsModel> {
    return this.apiService.patch('/company_integrations', settings , 'company_integration');
  }

  saveDefaultUserTerminal ( terminal : TillpaymentsTerminalModel, user = this.sessionService.user() ) : ng.IPromise<any> {
    return this.apiService.post('/users/update_terminal_id', {
      id         : user.id,
      terminal_id: terminal.terminalId
    });
  }

  /**
   * A surcharged payment on a company with an accounting package closes a few seconds after
   * the process call returns, so reloading straight away still shows it Open.
  **/
  waitForPaymentToClose ( payment : CustomerPaymentModel, attemptsLeft = 15 ) : Promise<CustomerPaymentModel> {
    if (!payment || !payment.id || attemptsLeft <= 0 || this.customerPaymentsService.isStatus(payment, 'closed')) {
      return Promise.resolve(payment);
    }

    // Keep the screen busy between polls so Process cannot be pressed again mid-wait.
    window['isResolving'](true);

    return new Promise(resolve => setTimeout(resolve, 2000))
    .then(() => this.apiService.get('/customer_payment', payment.id))
    .then(( latest : CustomerPaymentModel ) => this.waitForPaymentToClose(latest, attemptsLeft - 1))
    .catch(() => payment);
  }
}